import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { NavigationHelper } from '../../helpers/navigation';
import { PottyNotesConstants } from '../../potty-notes-consts';
import { ErrorModalService } from '../../../../src/app/services/error-modal.service';
import { Router } from '@angular/router';
import { PottyNotesGestureController } from '../../utils/gesture.controller';
import { NoteService } from '../../services/note.service';

@Component({
    selector: 'app-report-card',
    templateUrl: './report-card.component.html',
    styleUrls: ['./report-card.component.scss'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-in', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                animate('300ms ease-out', style({ opacity: 0 })),
            ]),
        ]),
    ],
    standalone: false
})
export class ReportCardComponent implements OnInit, AfterViewInit {
	public static readonly ComponentIdentifier: number = 6;
	public isStartOfFlow: boolean;
	public forwardButtonText: string;
	public totalOverallRating = 0;
	public normalizedOverallRating = 0.0;
	public allRatingsSelected = false;
	public skipButtonText: string;
	public isSuccessModalVisible = false;

	public dataForRatings = [
		{
			title: 'Ambiance',
			messages: [
				PottyNotesConstants.ambianceOne,
				PottyNotesConstants.ambianceTwo,
				PottyNotesConstants.ambianceThree,
				PottyNotesConstants.ambianceFour,
				PottyNotesConstants.ambianceFive
			],
			isSelected: false
		},
		{
			title: 'Cleanliness',
			messages: [
				PottyNotesConstants.cleanlinessOne,
				PottyNotesConstants.cleanlinessTwo,
				PottyNotesConstants.cleanlinessThree,
				PottyNotesConstants.cleanlinessFour,
				PottyNotesConstants.cleanlinessFive
			],
			isSelected: false
		},
		{
			title: 'Toilet Paper',
			messages: [
				PottyNotesConstants.toiletPaperOne,
				PottyNotesConstants.toiletPaperTwo,
				PottyNotesConstants.toiletPaperThree,
				PottyNotesConstants.toiletPaperFour,
				PottyNotesConstants.toiletPaperFive
			],
			isSelected: false
		},
		{
			title: 'Privacy',
			messages: [
				PottyNotesConstants.privacyOne,
				PottyNotesConstants.privacyTwo,
				PottyNotesConstants.privacyThree,
				PottyNotesConstants.privacyFour,
				PottyNotesConstants.privacyFive
			],
			isSelected: false
		},
		{
			title: 'Comfort',
			messages: [
				PottyNotesConstants.comfortOne,
				PottyNotesConstants.comfortTwo,
				PottyNotesConstants.comfortThree,
				PottyNotesConstants.comfortFour,
				PottyNotesConstants.comfortFive
			],
			isSelected: false
		}
	]

	private initialTouchX: number | null = null;
	private initialTouchY: number | null = null;
	private touchStartTime: number | null = null;
	private totalNumberOfChoices: number = 25.0;
	// The number we normalize to.
	private scaleFactor: number = 5.0;

	@ViewChild('categoriesWrapper') subratingsRef: ElementRef;

	constructor(public errorModalService: ErrorModalService,
		private navigationHelper: NavigationHelper,
		private router: Router,
		public noteService: NoteService) {}

	ngAfterViewInit(): void {
		setTimeout(() => {
			this.restoreScrollPosition();
		}, 200);
	}
		
	ngOnInit(): void {
		this.isStartOfFlow = this.navigationHelper.isStartOfNoteFlow();
		this.forwardButtonText = this.navigationHelper.isEndOfNoteFlow() ? 'Submit Note' : 'Onto the next one';
		this.normalizedOverallRating = this.normalizeOverallRating(this.noteService.totalReportCardRating);
		this.noteService.reportCardRatings.map(rating => {
			this.markRatingAsSelected(rating.category);
		})
	}

	public handleOverallRatingState(rating: number) {
		this.noteService.totalReportCardRating += rating;
		this.normalizedOverallRating = this.normalizeOverallRating(this.noteService.totalReportCardRating);
		this.noteService.reportCardRatings.filter(r => r.category == 'Overall Rating')[0].rating = this.normalizedOverallRating;
	}

	private normalizeOverallRating(totalReportCardRating: number) : number {
		return +((totalReportCardRating / this.totalNumberOfChoices) * this.scaleFactor).toFixed(2);
	}

	public markRatingAsSelected(title: string) {
		this.dataForRatings = this.dataForRatings.map(rating => {
			if (rating.title === title) {
				rating.isSelected = true;
			}
			return rating;
		})

		if (this.dataForRatings.every(rating => rating.isSelected)) {
			this.allRatingsSelected = true;
		}
	}

	public trackIndividualCategoryRating(titleAndRating: string) {
		let info  = titleAndRating.split('|');
		let categoryKey = info[0];
		let individualRating = info[1];
		this.noteService.reportCardRatings.filter(r => r.category == categoryKey)[0].rating = +individualRating;

		this.setDescription(categoryKey, +individualRating);

		// The isSkipped is handled down stream in the note service sendCreateNoteRequest(). 
		this.noteService.setReportCard(false);
	}

	private setDescription(categoryKey: string, ratingValue: number) {
		const category = this.dataForRatings.find(rating => rating.title === categoryKey);
		const description = category?.messages[ratingValue - 1] || null;
		
		this.noteService.reportCardRatings.filter(r => r.category == categoryKey)[0].description = description;
	}
	

	public showSuccessModal() {
		this.isSuccessModalVisible = true;
		document.body.style.pointerEvents = 'none';
	}

	public closeSuccessModal() {
		this.isSuccessModalVisible = false;
		document.body.style.pointerEvents = 'auto';
	}

	public getRatingForCategory(category: string): number {
		return this.noteService.reportCardRatings
			.find(r => r.category === category)?.rating || 0;
	}

	private saveScrollPosition() {
		this.noteService.reportCardScrollPosition = this.subratingsRef.nativeElement.querySelector('.categories').scrollTop;
	}

	private restoreScrollPosition() {
		const categoriesElement = this.subratingsRef.nativeElement.querySelector('.categories');
		if (categoriesElement) {
			categoriesElement.scrollTop = this.noteService.reportCardScrollPosition;
		}
	}

	// Gesture control 
	@HostListener('touchstart', ['$event'])
	public onTouchStart(event: TouchEvent): void {
		if (this.ignoreArea(event)) return;
		this.initialTouchX = event.touches[0].clientX;
		this.initialTouchY = event.touches[0].clientY;
		this.touchStartTime = Date.now();
	}

	@HostListener('touchmove', ['$event'])
	public onTouchMove(event: TouchEvent): void {
		if (this.ignoreArea(event)) return;

		if (this.initialTouchX === null || this.touchStartTime === null || this.initialTouchY == null) return;

		const currentTouchX = event.touches[0].clientX;
		const currentTouchY = event.touches[0].clientY;
		const deltaX = currentTouchX - this.initialTouchX;
		const deltaY = currentTouchY - this.initialTouchY;
		const timeDiff = Date.now() - this.touchStartTime

		// This represents moving backward in the add notes flow.
		if (PottyNotesGestureController.MovedRightEnough(timeDiff, deltaX, deltaY)) {
			event.preventDefault();
			this.saveScrollPosition();
			this.router.navigate([`${this.navigationHelper.getPreviousComponentUrl()}`]);
		}
	}

	@HostListener('touchend', ['$event'])
	public onTouchEnd(event: TouchEvent): void {
		event.stopPropagation();
	}

	private ignoreArea(event: TouchEvent) {
		if (this.subratingsRef === undefined) return true;

		const rect = this.subratingsRef.nativeElement.getBoundingClientRect();

		const touch = event.touches[0];
		return (
			touch.clientX >= rect.left &&
			touch.clientX <= rect.right &&
			touch.clientY >= rect.top &&
			touch.clientY <= rect.bottom
		  )
	}
}
